import { useCallback, useEffect, useState } from 'react'
import type { DailyMetrics, ProductLevel, ProductRecord } from './types'

export interface RealtimeSummary {
  gmv: number
  soldItemCount: number
  orderCount: number
  customerCount: number
  productVisitors: number
  productPageviews: number
  conversionRate: number
  activeProducts: number
  rowCount: number
}

export interface RealtimeSnapshot {
  runId: string
  snapshotAt: string
  pageUpdatedAt: string
  collectedAt: string
  spu: RealtimeSummary
  sku: RealtimeSummary
  skuExportLimitReached: boolean
}

export interface RealtimeProductMetrics {
  gmv: number
  soldItemCount: number
  orderCount: number
  customerCount: number
  conversionRate: number
  averageOrderValue: number
  averageItemPrice: number
  productPageviews: number
  productVisitors: number
  cartItemCount: number
  cartCustomerCount: number
  cartAmount: number
  cancelOrderAmount: number
}

export interface RealtimeProduct {
  level: ProductLevel
  id: string
  name: string
  sourceName: string
  spuId?: string
  merchantSku?: string
  image?: string
  category: string[]
  metrics: RealtimeProductMetrics
  previousMetrics: Pick<
    RealtimeProductMetrics,
    'gmv' | 'soldItemCount' | 'productPageviews' | 'productVisitors' | 'conversionRate'
  > | null
}

export interface RealtimeProductData {
  schemaVersion: string
  meta: {
    storeName: string
    businessDate: string
    latestRunId: string
    latestSnapshotAt: string
    pageUpdatedAt: string
    collectedAt: string
    generatedAt: string
    sourceSystem: string
    pageId: string
    granularity: string
    attribution: string
    snapshotCount: number
    skuExportLimitReached: boolean
    warnings: string[]
  }
  summary: RealtimeSummary
  previousSummary: RealtimeSummary | null
  snapshots: RealtimeSnapshot[]
  products: RealtimeProduct[]
}

export type RealtimeProductSortKey =
  | 'gmv'
  | 'delta'
  | 'soldItemCount'
  | 'averageItemPrice'
  | 'productVisitors'
  | 'productPageviews'
  | 'conversionRate'
  | 'averageOrderValue'

export const realtimeMetricsToDaily = (
  metrics: Partial<RealtimeProductMetrics>,
  date: string,
): DailyMetrics => ({
  date,
  gmv: metrics.gmv || 0,
  soldItemCount: metrics.soldItemCount || 0,
  orderCount: metrics.orderCount || 0,
  customerCount: metrics.customerCount || 0,
  conversionRate: metrics.conversionRate || 0,
  averageOrderValue: metrics.averageOrderValue || 0,
  averageItemPrice: metrics.averageItemPrice || 0,
  productVisitors: metrics.productVisitors || 0,
  productPageviews: metrics.productPageviews || 0,
  searchImpressions: 0,
  searchClicks: 0,
  cartItemCount: metrics.cartItemCount || 0,
  cancelRefundAmount: 0,
  cancelRefundItemCount: 0,
})

const categoryTuple = (category: string[]): [string, string, string] => [
  category[0] || '',
  category[1] || '',
  category[2] || '',
]

export const realtimeProductToRecord = (
  product: RealtimeProduct,
  existing?: ProductRecord,
): ProductRecord => existing || {
  level: product.level,
  id: product.id,
  sourceName: product.sourceName,
  name: product.name,
  category: categoryTuple(product.category),
  itemNumber: '',
  spuId: product.level === 'spu' ? product.id : product.spuId || '',
  jdSkuId: product.level === 'sku' ? product.id : '',
  merchantSku: product.merchantSku || '',
  image: product.image || '',
  imageLabel: product.image ? '商品资料图' : '',
  productUrl: '',
  history: [],
}

async function fetchRealtimeData() {
  const response = await fetch('/data/realtime-product-data.json', { cache: 'no-store' })
  if (!response.ok) throw new Error(`实时数据加载失败：${response.status}`)
  return response.json() as Promise<RealtimeProductData>
}

export function useRealtimeData() {
  const [data, setData] = useState<RealtimeProductData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const reload = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(await fetchRealtimeData())
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  return { data, loading, error, reload }
}
